import React, { CSSProperties } from 'react';
import { WithStyle } from 'rewire-ui';
import { observable, Observe } from 'rewire-core';
import classNames from 'classnames';
import { Theme } from '@material-ui/core/styles';
import Snackbar, { SnackbarOrigin } from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import Slide from '@material-ui/core/Slide';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/WarningRounded';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { MessageLevel } from '../DataTypes';
import { withStyles } from '../utility/styles';

const styles = (theme: Theme) => ({
  success: { color: theme.palette.success.contrastText, backgroundColor: theme.palette.success.main },
  error: { color: theme.palette.error.contrastText, backgroundColor: theme.palette.error.main },
  info: { color: theme.palette.info.contrastText, backgroundColor: theme.palette.info.main },
  warning: { color: theme.palette.warning.contrastText, backgroundColor: theme.palette.warning.main },
  icon: { fontSize: 24 },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  snackbar: {
    position: 'relative',
    cursor: 'pointer',
  },
  snackbarContainer: {
    position: 'fixed',
    zIndex: 1400,
  },
});

interface INotification {
  message: string;
  open: boolean;
  key: number;
  level: MessageLevel;
  icon: React.ComponentType<SvgIconProps>;
}

interface INotificationRenderer {
  anchorVertical?: 'top' | 'bottom';
  anchorHorizontal?: 'left' | 'center' | 'right';
  displayDuration?: number;
}

const DEFAULT_ANCHOR_VERTICAL = 'bottom';
const DEFAULT_ANCHOR_HORIZONTAL = 'right';
const DEFAULT_DISPLAY_DURATION = 6_000;
let NOTIFICATION_KEY = 1;

type NotificationProps = WithStyle<ReturnType<typeof styles>, INotificationRenderer>;

class Notifications {
  public readonly notifications: INotification[] = observable([]);

  info(message: string): void {
    this.notifications.push({
      message,
      open: true,
      key: NOTIFICATION_KEY++,
      level: 'info',
      icon: InfoIcon,
    });
  }

  success(message: string): void {
    this.notifications.push({
      message,
      open: true,
      key: NOTIFICATION_KEY++,
      level: 'success',
      icon: CheckCircleIcon,
    });
  }

  warning(message: string): void {
    this.notifications.push({
      message,
      open: true,
      key: NOTIFICATION_KEY++,
      level: 'warning',
      icon: WarningIcon,
    });
  }

  error(message: any): void {
    message =
      (message?.error?.errors?.length && message.error.errors[0].message) || // remove this when rewire-graphql is rebuilt
      (message?.error?.length && message.error[0].message) ||
      (message?.errors?.length && message.errors[0].message) ||
      message?.message ||
      message ||
      '';
    if (!message) return;

    this.notifications.push({
      message,
      open: true,
      key: NOTIFICATION_KEY++,
      level: 'error',
      icon: ErrorIcon,
    });
  }

  remove(notification: INotification): void {
    const idx = this.notifications.indexOf(notification);
    if (idx !== -1) {
      this.notifications.splice(idx, 1);
    }
  }

  get count(): number {
    return this.notifications.length;
  }
}

export const notifications = new Notifications();

class NotificationsRenderer extends React.PureComponent<NotificationProps> {
  handleClose = (notification: INotification) => {
    notification.open = false;
  };

  handleExited = (notification: INotification) => {
    notifications.remove(notification);
  };

  styleFn(idx: number) {
    const styles: CSSProperties = {};
    if (this.props.anchorHorizontal === 'left') {
      styles['left'] = '0px';
    } else if (this.props.anchorHorizontal === 'right') {
      styles['right'] = '0px';
    } else {
      styles['left'] = '50%';
      styles['transform'] = 'translateX(-50%)';
    }
    if (this.props.anchorVertical === 'bottom') {
      styles['bottom'] = `${(notifications.count - idx - 1) * 58}px`;
    } else {
      styles['top'] = `${(notifications.count - idx - 1) * 58}px`;
    }
    return styles;
  }

  renderSnackbar = React.memo(
    (props: { notification: INotification; anchorOrigin: SnackbarOrigin; displayDuration: number }): JSX.Element => {
      const { classes } = this.props;
      const { notification, anchorOrigin, displayDuration } = props;
      const key = notification.key;
      return (
        <Observe
          render={() => (
            <Snackbar
              key={key}
              className={classes.snackbar}
              anchorOrigin={anchorOrigin}
              open={notification.open}
              autoHideDuration={displayDuration}
              TransitionComponent={Slide}
              onClose={() => this.handleClose(notification)}
              onExited={() => this.handleExited(notification)}
              ClickAwayListenerProps={{ mouseEvent: false }}
              onClick={() => this.handleClose(notification)}
            >
              <SnackbarContent
                message={
                  <div className={classes.message}>
                    <notification.icon className={classNames(classes.icon, classes.iconVariant)} />
                    {notification.message}
                  </div>
                }
                className={classes[notification.level]}
              />
            </Snackbar>
          )}
        />
      );
    },
    () => true,
  );

  render() {
    const { classes } = this.props;
    let { anchorVertical, anchorHorizontal, displayDuration } = this.props;
    if (!anchorVertical) anchorVertical = DEFAULT_ANCHOR_VERTICAL;
    if (!anchorHorizontal) anchorHorizontal = DEFAULT_ANCHOR_HORIZONTAL;
    if (!displayDuration) displayDuration = DEFAULT_DISPLAY_DURATION;
    return (
      <Observe
        render={() =>
          notifications.count > 0 ? (
            <>
              {notifications.notifications.map((notification, idx) => (
                <div key={notification.key} className={classes.snackbarContainer} style={this.styleFn(idx)}>
                  <this.renderSnackbar
                    notification={notification}
                    anchorOrigin={{ vertical: anchorVertical!, horizontal: anchorHorizontal! }}
                    displayDuration={displayDuration!}
                  />
                </div>
              ))}
            </>
          ) : null
        }
      />
    );
  }
}

export default withStyles<INotificationRenderer>(styles, NotificationsRenderer);
